'use client';

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useRouter } from 'next/navigation';

interface PropertySubmissionFormProps {
  className?: string;
}

interface FormData {
  propertyAddress: string;
  city: string;
  state: string;
  zipCode: string;
  propertyType: string;
  bedrooms: string;
  bathrooms: string;
  squareFootage: string;
  situation: string;
  timeframe: string;
  propertyCondition: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferredContactMethod: string;
  additionalDetails: string;
}

const PropertySubmissionForm = ({ className = '' }: PropertySubmissionFormProps) => {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    propertyAddress: '',
    city: '',
    state: '',
    zipCode: '',
    propertyType: '',
    bedrooms: '',
    bathrooms: '',
    squareFootage: '',
    situation: '',
    timeframe: '',
    propertyCondition: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    preferredContactMethod: '',
    additionalDetails: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Pre-fill from homepage form if user came via /#hero submission
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('initialLeadData');
      if (!stored) return;
      const initial = JSON.parse(stored) as {
        propertyAddress?: string;
        name?: string;
        phoneNumber?: string;
        email?: string;
      };
      const nameParts = (initial.name || '').trim().split(/\s+/);
      const firstName = nameParts.shift() || '';
      const lastName = nameParts.join(' ');
      setFormData((prev) => ({
        ...prev,
        propertyAddress: initial.propertyAddress || prev.propertyAddress,
        firstName: firstName || prev.firstName,
        lastName: lastName || prev.lastName,
        phone: initial.phoneNumber || prev.phone,
        email: initial.email || prev.email,
      }));
      sessionStorage.removeItem('initialLeadData');
    } catch {
      // If parse fails, just ignore and start fresh
    }
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    setStep((prev) => Math.min(prev + 1, 3));
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error: submitError } = await supabase.from('seller_leads').insert({
        property_address: formData.propertyAddress,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        property_type: formData.propertyType || null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
        square_footage: formData.squareFootage ? parseInt(formData.squareFootage) : null,
        situation: formData.situation || null,
        timeframe: formData.timeframe || null,
        property_condition: formData.propertyCondition || null,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone || null,
        preferred_contact_method: formData.preferredContactMethod || null,
        additional_details: formData.additionalDetails || null,
      });

      if (submitError) {
        throw submitError;
      }

      // Show success message instead of redirecting
      setSubmitted(true);
    } catch (err: any) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="property-form" className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="max-w-4xl mx-auto px-4 lg:px-8">
        {submitted ? (
          <div className="text-center">
            <div className="bg-card rounded-xl shadow-card border border-border p-12">
              <div className="mb-6">
                <svg
                  className="w-20 h-20 text-primary mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
                Thank you for submitting!
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We've received your property information and will contact you shortly with a cash
                offer.
              </p>
              <button
                onClick={() => router.push('/')}
                className="bg-primary text-white py-3 px-8 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                Return to Home
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Form Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-6">
                Get Your Cash Offer Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Fill out our secure form to receive a fair, no-obligation cash offer for your
                property.
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex justify-between items-center">
                {[1, 2, 3].map((num) => (
                  <div key={num} className="flex items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                        step >= num ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {num}
                    </div>
                    {num < 3 && (
                      <div
                        className={`flex-1 h-1 mx-2 ${step > num ? 'bg-primary' : 'bg-gray-200'}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                <span>Property Info</span>
                <span>Property Details</span>
                <span>Contact Info</span>
              </div>
            </div>

            {/* Form Container */}
            <div className="bg-card rounded-xl shadow-card border border-border p-8">
              <form onSubmit={handleSubmit}>
                {/* Step 1: Property Information */}
                {step === 1 && (
                  <div className="space-y-6">
                    <div>
                      <label
                        htmlFor="propertyAddress"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        Property Address *
                      </label>
                      <input
                        type="text"
                        id="propertyAddress"
                        name="propertyAddress"
                        value={formData.propertyAddress}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label
                          htmlFor="city"
                          className="block text-sm font-medium text-foreground mb-2"
                        >
                          City *
                        </label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="state"
                          className="block text-sm font-medium text-foreground mb-2"
                        >
                          State *
                        </label>
                        <input
                          type="text"
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          required
                          maxLength={2}
                          placeholder="AZ"
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="zipCode"
                          className="block text-sm font-medium text-foreground mb-2"
                        >
                          ZIP Code *
                        </label>
                        <input
                          type="text"
                          id="zipCode"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleChange}
                          required
                          maxLength={10}
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="propertyType"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        Property Type
                      </label>
                      <select
                        id="propertyType"
                        name="propertyType"
                        value={formData.propertyType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select Type</option>
                        <option value="Single Family Home">Single Family Home</option>
                        <option value="Multi-Family">Multi-Family</option>
                        <option value="Condo">Condo</option>
                        <option value="Townhouse">Townhouse</option>
                        <option value="Land">Land</option>
                        <option value="Commercial">Commercial</option>
                      </select>
                    </div>

                    <button
                      type="button"
                      onClick={handleNext}
                      className="w-full bg-primary text-white py-4 px-6 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                    >
                      Next Step
                    </button>
                  </div>
                )}

                {/* Step 2: Property Details */}
                {step === 2 && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label
                          htmlFor="bedrooms"
                          className="block text-sm font-medium text-foreground mb-2"
                        >
                          Bedrooms
                        </label>
                        <input
                          type="number"
                          id="bedrooms"
                          name="bedrooms"
                          value={formData.bedrooms}
                          onChange={handleChange}
                          min="0"
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="bathrooms"
                          className="block text-sm font-medium text-foreground mb-2"
                        >
                          Bathrooms
                        </label>
                        <input
                          type="number"
                          id="bathrooms"
                          name="bathrooms"
                          value={formData.bathrooms}
                          onChange={handleChange}
                          min="0"
                          step="0.5"
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="squareFootage"
                          className="block text-sm font-medium text-foreground mb-2"
                        >
                          Square Footage
                        </label>
                        <input
                          type="number"
                          id="squareFootage"
                          name="squareFootage"
                          value={formData.squareFootage}
                          onChange={handleChange}
                          min="0"
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="situation"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        What is your situation?
                      </label>
                      <select
                        id="situation"
                        name="situation"
                        value={formData.situation}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select Situation</option>
                        <option value="Inherited Property">Inherited Property</option>
                        <option value="Relocating">Relocating</option>
                        <option value="Downsizing">Downsizing</option>
                        <option value="Financial Hardship">Financial Hardship</option>
                        <option value="Divorce">Divorce</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="timeframe"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        When do you need to sell?
                      </label>
                      <select
                        id="timeframe"
                        name="timeframe"
                        value={formData.timeframe}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select Timeframe</option>
                        <option value="ASAP">ASAP</option>
                        <option value="1-3 Months">1-3 Months</option>
                        <option value="3-6 Months">3-6 Months</option>
                        <option value="6+ Months">6+ Months</option>
                        <option value="Just Exploring">Just Exploring</option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="propertyCondition"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        Property Condition
                      </label>
                      <select
                        id="propertyCondition"
                        name="propertyCondition"
                        value={formData.propertyCondition}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select Condition</option>
                        <option value="Excellent Condition">Excellent Condition</option>
                        <option value="Good Condition">Good Condition</option>
                        <option value="Needs Repairs">Needs Repairs</option>
                        <option value="Major Repairs Needed">Major Repairs Needed</option>
                      </select>
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={handleBack}
                        className="flex-1 bg-gray-200 text-gray-700 py-4 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={handleNext}
                        className="flex-1 bg-primary text-white py-4 px-6 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                      >
                        Next Step
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Contact Information */}
                {step === 3 && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="firstName"
                          className="block text-sm font-medium text-foreground mb-2"
                        >
                          First Name *
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="lastName"
                          className="block text-sm font-medium text-foreground mb-2"
                        >
                          Last Name *
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="preferredContactMethod"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        Preferred Contact Method
                      </label>
                      <select
                        id="preferredContactMethod"
                        name="preferredContactMethod"
                        value={formData.preferredContactMethod}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select Method</option>
                        <option value="Email">Email</option>
                        <option value="Phone">Phone</option>
                        <option value="Text">Text</option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="additionalDetails"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        Additional Details
                      </label>
                      <textarea
                        id="additionalDetails"
                        name="additionalDetails"
                        value={formData.additionalDetails}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Tell us more about your property or situation..."
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    )}

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={handleBack}
                        className="flex-1 bg-gray-200 text-gray-700 py-4 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-primary text-white py-4 px-6 rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Submitting...' : 'Submit'}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default PropertySubmissionForm;
